import { drawImageFit } from "../utils/canvasUtils";

function getCanvasFilter(type) {
  switch (type) {
    case "grayscale":
      return "grayscale(1)";
    case "sepia":
      return "sepia(1)";
    case "contrast":
      return "contrast(1.4)";
    case "warm":
      return "brightness(1.05) saturate(1.3)";
    case "cool":
      return "brightness(0.95) saturate(0.9) hue-rotate(180deg)";
    case "vintage":
      return "sepia(0.6) contrast(1.2) saturate(0.8)";
    default:
      return "none";
  }
}

// Draws image clipped to a horizontal slice
function drawSlice(ctx, canvas, y, h, dx) {
  ctx.drawImage(
    canvas,                 // ✅ source is the fitted canvas
    0, y, canvas.width, h,  // source slice
    dx, y, canvas.width, h // destination slice
  );
}



// Channel shift helper
function drawChannelShift(ctx, source, rx, gx, bx) {
  ctx.globalCompositeOperation = "lighter";

  // R
  ctx.filter = "brightness(100%)";
  ctx.globalAlpha = 1;
  ctx.drawImage(source, rx, 0, ctx.canvas.width, ctx.canvas.height);

  // G
  ctx.filter = "sepia(100%) hue-rotate(80deg)";
  ctx.drawImage(source, gx, 0, ctx.canvas.width, ctx.canvas.height);

  // B
  ctx.filter = "sepia(100%) hue-rotate(180deg)";
  ctx.drawImage(source, bx, 0, ctx.canvas.width, ctx.canvas.height);

  ctx.filter = "none";
  ctx.globalCompositeOperation = "source-over";
}


// Ease curve for smoother transitions
const easeIn = (p) => p * p;

// Helper: draw image scaled around center
const drawScaled = (ctx, img, scale, canvas) => {
  drawImageFit(ctx, img, canvas, scale);
};


// Helper: generate heavy fog noise
const makeFog = (ctx, canvas, opacity = 0.5) => {
  const imgData = ctx.createImageData(canvas.width, canvas.height);
  const d = imgData.data;
  for (let i = 0; i < d.length; i += 4) {
    const n = 200 + Math.random() * 40;
    d[i] = n;     // r
    d[i+1] = n;   // g
    d[i+2] = n;   // b
    d[i+3] = 255 * opacity;
  }
  return imgData;
};


// Fade transition
export const drawFade = (
  ctx,
  imgA,
  imgB,
  p,
  canvas,
  scaleA = 1,
  scaleB = 1,
  oxA = 0,
  oyA = 0,
  oxB = 0,
  oyB = 0,
 filterA, filterB
) => {
 
  // --- Draw A ---
  ctx.save();
  ctx.globalAlpha = 1 - p;
  ctx.filter = getCanvasFilter(filterA);
  drawImageFit(ctx, imgA, canvas, scaleA, oxA, oyA);
  ctx.restore();

  // --- Draw B ---
  ctx.save();
  ctx.globalAlpha = p;
  ctx.filter = getCanvasFilter(filterB);
  drawImageFit(ctx, imgB, canvas, scaleB, oxB, oyB);
  ctx.restore();
};



// Slide left / right
export const drawSlide = (
  ctx,
  imgA,
  imgB,
  p,
  canvas,
  direction = -1,
  scaleA = 1,
  scaleB = 1,
  oxA = 0,
  oyA = 0,
  oxB = 0,
  oyB = 0,
 filterA,
  filterB
) => {
 
  const slide = canvas.width * p * direction;

  // A slides out
  ctx.save();
  ctx.globalAlpha = 1 - p;
  ctx.filter = getCanvasFilter(filterA);
  drawImageFit(
    ctx,
    imgA,
    canvas,
    scaleA,
    oxA - slide,
    oyA
  );
  ctx.restore();

  // B slides in
  ctx.save();
  ctx.globalAlpha = 1 - p;
  ctx.filter = getCanvasFilter(filterB);
  drawImageFit(
    ctx,
    imgB,
    canvas,
    scaleB,
    oxB + canvas.width * direction - slide,
    oyB
  );
  ctx.restore();
};



// Zoom-in
export const drawZoom = (
  ctx,
  imgA,
  imgB,
  p,
  canvas,
  scaleA = 1,
  scaleB = 1,
  oxA = 0,
  oyA = 0,
  oxB = 0,
  oyB = 0,
 filterA, filterB
) => {
  

  
  // Extra cinematic zoom on A
  const zoomOut = 1 + p * 0.25;

   ctx.save();
  ctx.globalAlpha = 1 - p;
  ctx.filter = getCanvasFilter(filterA);
  drawImageFit(
    ctx,
    imgA,
    canvas,
    scaleA * zoomOut,
    oxA,
    oyA
  );
  ctx.restore();

  ctx.save();
  ctx.globalAlpha = p;
  ctx.filter = getCanvasFilter(filterB);
  drawImageFit(
    ctx,
    imgB,
    canvas,
    scaleB,
    oxB,
    oyB
  );
ctx.restore();
  ctx.globalAlpha = 1;
};



export const drawBounce = (
  ctx,
  imgA,
  imgB,
  p,
  canvas,
  scaleA = 1,
  scaleB = 1,
  oxA = 0,
  oyA = 0,
  oxB = 0,
  oyB = 0,
 filterA, filterB
) => {
  // Elastic bounce curve
  const bounce = Math.sin(p * Math.PI);

  // Relative bounce (never override editor zoom)
  const outScale = scaleA * (1 + bounce * 0.15);
  const inScale  = scaleB * Math.max(0.001, p + bounce * 0.15);

  // A bouncing out
   ctx.save();
  ctx.globalAlpha = 1 - p;
  ctx.filter = getCanvasFilter(filterA);
  drawImageFit(
    ctx,
    imgA,
    canvas,
    outScale,
    oxA,
    oyA
  );
ctx.restore();

  // B bouncing in
  ctx.save();
  ctx.globalAlpha = p;
  ctx.filter = getCanvasFilter(filterB);

  drawImageFit(
    ctx,
    imgB,
    canvas,
    inScale,
    oxB,
    oyB
  );
  ctx.restore();
  ctx.globalAlpha = 1;
};



export const drawFlip = (
  ctx,
  imgA,
  imgB,
  p,
  canvas,
  scaleA = 1,
  scaleB = 1,
  oxA = 0,
  oyA = 0,
  oxB = 0,
  oyB = 0,
 filterA, filterB
) => {
  const angle = p * Math.PI;
  const flipScale = Math.cos(angle);

  ctx.save();
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.scale(flipScale, 1);

  if (p < 0.5) {
    // A flipping out
     ctx.save();
      ctx.globalAlpha = 1 - p;
      ctx.filter = getCanvasFilter(filterA);
    drawImageFit(
      ctx,
      imgA,
      canvas,
      scaleA,
      oxA - canvas.width / 2,
      oyA - canvas.height / 2
    );
    ctx.restore();
  } else {
    // B flipping in
    ctx.save();
    ctx.globalAlpha = p;
    ctx.filter = getCanvasFilter(filterB);
    drawImageFit(
      ctx,
      imgB,
      canvas,
      scaleB,
      oxB - canvas.width / 2,
      oyB - canvas.height / 2
    );
    ctx.restore();
  }

  ctx.restore();
};



export const drawWipe = (
  ctx,
  imgA,
  imgB,
  p,
  canvas,
  scaleA = 1,
  scaleB = 1,
  oxA = 0,
  oyA = 0,
  oxB = 0,
  oyB = 0,
  filterA ,
  filterB 
) => {
  // 1️⃣ Draw A fully (NO alpha for wipe)
  ctx.save();
  ctx.globalAlpha = 1;
  ctx.filter = getCanvasFilter(filterA);

  drawImageFit(ctx, imgA, canvas, scaleA, oxA, oyA);
  ctx.restore();

  // 2️⃣ Reveal B using clip mask
  ctx.save();
  ctx.globalAlpha = 1;
  ctx.filter = getCanvasFilter(filterB);

  ctx.beginPath();
  ctx.rect(0, 0, canvas.width * p, canvas.height);
  ctx.clip();

  drawImageFit(ctx, imgB, canvas, scaleB, oxB, oyB);
  ctx.restore();
};




export const drawBlurFade = (
  ctx,
  imgA,
  imgB,
  p,
  canvas,
  scaleA = 1,
  scaleB = 1,
  oxA = 0,
  oyA = 0,
  oxB = 0,
  oyB = 0,
 filterA, filterB
) => {
  // A blur-out
   ctx.save();
  ctx.filter = `blur(${p * 10}px)`;
  ctx.globalAlpha = 1 - p;
   ctx.globalAlpha = 1 - p;
 ctx.filter = getCanvasFilter(filterA);

  drawImageFit(
    ctx,
    imgA,
    canvas,
    scaleA,
    oxA,
    oyA
  );
ctx.restore();
  // B blur-in
  ctx.save();
  ctx.filter = `blur(${(1 - p) * 10}px)`;
  ctx.globalAlpha = p;
  ctx.filter = getCanvasFilter(filterB);

  drawImageFit(
    ctx,
    imgB,
    canvas,
    scaleB,
    oxB,
    oyB
  );

  ctx.filter = "none";
  ctx.globalAlpha = 1;
  ctx.restore();
};


export const drawGlitch = (
  ctx,
  imgA,
  imgB,
  p,
  canvas,
  scaleA = 1,
  scaleB = 1,
  oxA = 0,
  oyA = 0,
  oxB = 0,
  oyB = 0,
   filterA, filterB
) => {
  // 1️⃣ Draw A correctly
   ctx.save();
    ctx.filter = getCanvasFilter(filterA);
  drawImageFit(ctx, imgA, canvas, scaleA, oxA, oyA);
  ctx.restore();


  // 2️⃣ Glitch the FITTED canvas
  const slices = 10;
  for (let i = 0; i < slices; i++) {
    const y = Math.random() * canvas.height;
    const h = 5 + Math.random() * 30;
    const dx = (Math.random() - 0.5) * 40;

    ctx.drawImage(
      canvas,
      0, y, canvas.width, h,
      dx, y, canvas.width, h
    );
  }

  // 3️⃣ Fade B in (correctly transformed)
  ctx.save();
  ctx.globalAlpha = p;
  ctx.filter = getCanvasFilter(filterB);
  drawImageFit(ctx, imgB, canvas, scaleB, oxB, oyB);
  ctx.restore();
  ctx.globalAlpha = 1;
};


export const drawParallax = (
  ctx,
  imgA,
  imgB,
  p,
  canvas,
  scaleA = 1,
  scaleB = 1,
  oxA = 0,
  oyA = 0,
  oxB = 0,
  oyB = 0,
  filterA = "none",
  filterB = "none"
) => {
  
  const depth = 40;

  const shiftA = p * depth;
  const shiftB = (1 - p) * depth;

  // 1️⃣ Background (A) — slower movement
  ctx.save();
  ctx.globalAlpha = 1;
  ctx.filter = getCanvasFilter(filterA);

  drawImageFit(
    ctx,
    imgA,
    canvas,
    scaleA,
    oxA + shiftA,
    oyA
  );
  ctx.restore();

  // 2️⃣ Foreground (B) — opposite direction
  ctx.save();
  ctx.globalAlpha = p; // subtle fade-in feels natural
  ctx.filter = getCanvasFilter(filterB);

  drawImageFit(
    ctx,
    imgB,
    canvas,
    scaleB,
    oxB - shiftB,
    oyB
  );
  ctx.restore();
};




export const drawDirectionalFade = (
  ctx,
  imgA,
  imgB,
  p,
  canvas,
  scaleA = 1,
  scaleB = 1,
  oxA = 0,
  oyA = 0,
  oxB = 0,
  oyB = 0,
    filterA = "none",
  filterB = "none"
) => {
  
  // Base A
  drawImageFit(
    ctx,
    imgA,
    canvas,
    scaleA,
    oxA,
    oyA
  );

  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, canvas.width * p, canvas.height);
  ctx.clip();

  ctx.globalAlpha = p;

  drawImageFit(
    ctx,
    imgB,
    canvas,
    scaleB,
    oxB,
    oyB
  );

  ctx.restore();
  ctx.globalAlpha = 1;
};



export const drawGlitchStandard = (
  ctx,
  imgA,
  imgB,
  p,
  canvas,
  scaleA = 1,
  scaleB = 1,
  oxA = 0,
  oyA = 0,
  oxB = 0,
  oyB = 0,
 filterA, filterB
) => {
  // 1️⃣ Base A (editor-aware)
   ctx.save();

 ctx.filter = getCanvasFilter(filterA);
  drawImageFit(ctx, imgA, canvas, scaleA, oxA, oyA);
  ctx.restore();

  // 2️⃣ Slice the FITTED canvas
  const slices = 12;
  for (let i = 0; i < slices; i++) {
    const y = Math.random() * canvas.height;
    const h = 10 + Math.random() * 30;
    const dx = (Math.random() - 0.5) * 40;

    ctx.drawImage(
      canvas,
      0, y, canvas.width, h,
      dx, y, canvas.width, h
    );
  }

  // 3️⃣ Fade in B
  ctx.save();
  ctx.globalAlpha = p;
  ctx.filter = getCanvasFilter(filterB);
  drawImageFit(ctx, imgB, canvas, scaleB, oxB, oyB);
  ctx.restore();
  ctx.globalAlpha = 1;
};


export const drawGlitchDigital = (
  ctx,
  imgA,
  imgB,
  p,
  canvas,
  scaleA = 1,
  scaleB = 1,
  oxA = 0,
  oyA = 0,
  oxB = 0,
  oyB = 0,
 filterA, filterB
) => {
  // Base A
   ctx.save();
 ctx.filter = getCanvasFilter(filterA);
  drawImageFit(ctx, imgA, canvas, scaleA, oxA, oyA);
  ctx.restore();

  // Horizontal slices (on fitted canvas)
  for (let i = 0; i < 8; i++) {
    const y = Math.random() * canvas.height;
    const h = 5 + Math.random() * 40;
    const dx = (Math.random() - 0.5) * 80;

    ctx.drawImage(
      canvas,
      0, y, canvas.width, h,
      dx, y, canvas.width, h
    );
  }

  // Pixel block distortion
  if (Math.random() < 0.4) {
    const block = [4, 6, 8][Math.floor(Math.random() * 3)];

    ctx.imageSmoothingEnabled = false;

    ctx.drawImage(
      canvas,
      0, 0,
      canvas.width, canvas.height,
      0, 0,
      canvas.width / block,
      canvas.height / block
    );

    ctx.drawImage(
      canvas,
      0, 0,
      canvas.width / block,
      canvas.height / block,
      0, 0,
      canvas.width,
      canvas.height
    );

    ctx.imageSmoothingEnabled = true;
  }

  // Fade in B
  ctx.save();
  ctx.globalAlpha = p;
  ctx.filter = getCanvasFilter(filterB);

  drawImageFit(ctx, imgB, canvas, scaleB, oxB, oyB);
  ctx.restore();
  ctx.globalAlpha = 1;
};



export const drawGlitchVHS = (
  ctx,
  imgA,
  imgB,
  p,
  canvas,
  scaleA = 1,
  scaleB = 1,
  oxA = 0,
  oyA = 0,
  oxB = 0,
  oyB = 0
) => {
  // Base A
  drawImageFit(ctx, imgA, canvas, scaleA, oxA, oyA);

  const max = 20 * p;

  drawChannelShift(
    ctx,
    canvas,
    (Math.random() - 0.5) * max,
    (Math.random() - 0.5) * max,
    (Math.random() - 0.5) * max
  );

  // Chroma jitter
  if (Math.random() < 0.3) {
    ctx.globalAlpha = 0.2;
    ctx.drawImage(
      canvas,
      (Math.random() - 0.5) * 10,
      0,
      canvas.width,
      canvas.height
    );
    ctx.globalAlpha = 1;
  }

  // Fade in B
  ctx.globalAlpha = p;
  drawImageFit(ctx, imgB, canvas, scaleB, oxB, oyB);
  ctx.globalAlpha = 1;
};



export const drawGlitchScanline = (
  ctx,
  imgA,
  imgB,
  p,
  canvas,
  scaleA = 1,
  scaleB = 1,
  oxA = 0,
  oyA = 0,
  oxB = 0,
  oyB = 0,
 filterA, filterB
) => {
  // Base A
   ctx.save();
 ctx.filter = getCanvasFilter(filterA);
  drawImageFit(ctx, imgA, canvas, scaleA, oxA, oyA);
  ctx.restore();

  const barHeight = 2;
  ctx.globalAlpha = 0.1 + p * 0.2;

  for (let y = 0; y < canvas.height; y += barHeight * 2) {
    ctx.fillStyle = "black";
    ctx.fillRect(0, y, canvas.width, barHeight);
  }

  // Fade B
  ctx.save();
ctx.globalAlpha = p;
ctx.filter = getCanvasFilter(filterB);
  drawImageFit(ctx, imgB, canvas, scaleB, oxB, oyB);
  ctx.restore();
  ctx.globalAlpha = 1;
};


export const drawGlitchAnalog = (
  ctx,
  imgA,
  imgB,
  p,
  canvas,
  scaleA = 1,
  scaleB = 1,
  oxA = 0,
  oyA = 0,
  oxB = 0,
  oyB = 0,
 filterA, filterB
) => {
  const amp = 15 * p;
  const freq = 0.03;

  // Draw A once
   ctx.save();
 ctx.filter = getCanvasFilter(filterA);
  drawImageFit(ctx, imgA, canvas, scaleA, oxA, oyA);
  ctx.restore();

  // Distort fitted canvas
  for (let y = 0; y < canvas.height; y++) {
    const offset = Math.sin(y * freq + performance.now() * 0.01) * amp;

    ctx.drawImage(
      canvas,
      0, y, canvas.width, 1,
      offset, y, canvas.width, 1
    );
  }

  // Fade B
  ctx.save();
ctx.globalAlpha = p;
ctx.filter = getCanvasFilter(filterB);
  drawImageFit(ctx, imgB, canvas, scaleB, oxB, oyB);
  ctx.restore();
  ctx.globalAlpha = 1;
};


let LAST_GLITCH_FRAME = null;

export const drawGlitchBuffer = (
  ctx,
  imgA,
  imgB,
  p,
  canvas,
  scaleA = 1,
  scaleB = 1,
  oxA = 0,
  oyA = 0,
  oxB = 0,
  oyB = 0,
 filterA, filterB
) => {
  // Base A
   ctx.save();
 ctx.filter = getCanvasFilter(filterA);
  drawImageFit(ctx, imgA, canvas, scaleA, oxA, oyA);
  ctx.restore();

  if (LAST_GLITCH_FRAME) {
    ctx.globalAlpha = 0.4;
    ctx.drawImage(
      LAST_GLITCH_FRAME,
      (Math.random() - 0.5) * 10,
      0,
      canvas.width,
      canvas.height
    );
    ctx.globalAlpha = 1;
  }

  // Capture fitted frame
  const snap = document.createElement("canvas");
  snap.width = canvas.width;
  snap.height = canvas.height;
  snap.getContext("2d").drawImage(canvas, 0, 0);
  LAST_GLITCH_FRAME = snap;

  // Fade B
  ctx.save();
ctx.globalAlpha = p;
ctx.filter = getCanvasFilter(filterB);
  drawImageFit(ctx, imgB, canvas, scaleB, oxB, oyB);
  ctx.restore();
  ctx.globalAlpha = 1;
};


const glitchTexture = new Image();
glitchTexture.src = "/textures/noise.png";

export const drawGlitchMultilayer = (ctx, imgA, imgB, p, canvas) => {
  drawImageFit(ctx, imgA, canvas);

  for (let i = 0; i < 5; i++) {
    const y = Math.random() * canvas.height;
    drawSlice(ctx, imgA, y, 30, (Math.random() - 0.5) * 100, canvas);
  }

  ctx.globalAlpha = 0.25 + p * 0.3;
  if (glitchTexture.complete) {
    ctx.drawImage(glitchTexture, 0, 0, canvas.width, canvas.height);
  }
  ctx.globalAlpha = 1;

  ctx.globalAlpha = p;
  drawImageFit(ctx, imgB, canvas);
  ctx.globalAlpha = 1;
};


export const drawFog = (
  ctx,
  imgA,
  imgB,
  p,
  canvas,
  scaleA = 1,
  scaleB = 1,
  oxA = 0,
  oyA = 0,
  oxB = 0,
  oyB = 0,
 filterA, filterB

) => {
  p = easeIn(p);

  // 1️⃣ Draw A with editor transform
   ctx.save();
  ctx.filter = getCanvasFilter(filterA);

  drawImageFit(
    ctx,
    imgA,
    canvas,
    scaleA,
    oxA,
    oyA
  );
  ctx.restore();

  // 2️⃣ Fog overlay (screen-space)
  const fogAlpha = 0.5 * p;
  const fog = makeFog(ctx, canvas, fogAlpha);

  ctx.save();
  ctx.globalCompositeOperation = "lighter";
  ctx.putImageData(fog, 0, 0);
  ctx.restore();

  // 3️⃣ Fade in B with editor transform
  ctx.save();
ctx.globalAlpha = p;
ctx.filter = getCanvasFilter(filterB);

  drawImageFit(
    ctx,
    imgB,
    canvas,
    scaleB,
    oxB,
    oyB
  );
  ctx.restore();
  ctx.globalAlpha = 1;
};


export const drawShrinkPop = (
  ctx,
  imgA,
  imgB,
  p,
  canvas,
  scaleA = 1,
  scaleB = 1,
  oxA = 0,
  oyA = 0,
  oxB = 0,
  oyB = 0,
 filterA, filterB
) => {
  const mid = 0.5;

  if (p < mid) {
    const t = p / mid;
    const shrink = Math.max(0.05, 1 - t);
     ctx.save();
 
    ctx.filter = getCanvasFilter(filterA);
    drawImageFit(
      ctx,
      imgA,
      canvas,
      scaleA * shrink,
      oxA,
      oyA
    );
    ctx.restore();
  } else {
    const t = (p - mid) / (1 - mid);
    const grow = Math.max(0.05, t);
    ctx.save();
  
    ctx.filter = getCanvasFilter(filterB);
    drawImageFit(
      ctx,
      imgB,
      canvas,
      scaleB * grow,
      oxB,
      oyB
    );
    ctx.restore();
  }
};


export const drawWhipPan = (
  ctx,
  imgA,
  imgB,
  p,
  canvas,
  scaleA = 1,
  scaleB = 1,
  oxA = 0,
  oyA = 0,
  oxB = 0,
  oyB = 0,
 filterA, filterB
) => {
  // Fast acceleration for whip feel
  const t = p * p;

  const maxShift = canvas.width * 2.5;
  const shift = maxShift * t;

  // --- A whips out ---
   ctx.save();

 ctx.filter = getCanvasFilter(filterA);
  drawImageFit(
    ctx,
    imgA,
    canvas,
    scaleA,
    oxA - shift,
    oyA
  );
  ctx.restore();

  // --- B whips in ---
  ctx.save();

ctx.filter = getCanvasFilter(filterB);

  drawImageFit(
    ctx,
    imgB,
    canvas,
    scaleB,
    oxB + canvas.width * 2.5 - shift,
    oyB
  );
  ctx.restore();
};



export const drawFlash = (
  ctx,
  imgA,
  imgB,
  p,
  canvas,
  scaleA = 1,
  scaleB = 1,
  oxA = 0,
  oyA = 0,
  oxB = 0,
  oyB = 0,
 filterA, filterB
) => {
  // Base A
   ctx.save();
 ctx.filter = getCanvasFilter(filterA);
  drawImageFit(ctx, imgA, canvas, scaleA, oxA, oyA);
  ctx.restore();

  // Flash burst
  const flash = Math.sin(p * Math.PI) * 1.4;
  ctx.fillStyle = `rgba(255,255,255,${flash})`;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Fade B
  ctx.save();
ctx.globalAlpha = p;
ctx.filter = getCanvasFilter(filterB);
  drawImageFit(ctx, imgB, canvas, scaleB, oxB, oyB);
  ctx.restore();
  ctx.globalAlpha = 1;
};


export const drawRipple = (
  ctx,
  imgA,
  imgB,
  p,
  canvas,
  scaleA = 1,
  scaleB = 1,
  oxA = 0,
  oyA = 0,
  oxB = 0,
  oyB = 0,
 filterA, filterB
) => {
  p = easeIn(p);
  const intensity = 10 * (1 - p);

  const w = canvas.width;
  const h = canvas.height;

  // 1️⃣ Draw A with editor transform
  ctx.save();
 ctx.filter = getCanvasFilter(filterA);
  drawImageFit(ctx, imgA, canvas, scaleA, oxA, oyA);
  ctx.restore();

  // 2️⃣ Read fitted frame
  const src = ctx.getImageData(0, 0, w, h);
  const dst = ctx.createImageData(w, h);

  const s = src.data;
  const d = dst.data;

  // 3️⃣ Ripple distortion (row-based)
  for (let y = 0; y < h; y++) {
    const offset = Math.sin(y / 10 + p * 8) * intensity;

    for (let x = 0; x < w; x++) {
      const sx = Math.min(
        w - 1,
        Math.max(0, Math.round(x + offset))
      );

      const si = (y * w + sx) * 4;
      const di = (y * w + x) * 4;

      d[di]     = s[si];
      d[di + 1] = s[si + 1];
      d[di + 2] = s[si + 2];
      d[di + 3] = s[si + 3];
    }
  }

  ctx.putImageData(dst, 0, 0);

  // 4️⃣ Fade in B (editor-aware)
  ctx.save();
ctx.globalAlpha = p;
ctx.filter = getCanvasFilter(filterB);
  drawImageFit(ctx, imgB, canvas, scaleB, oxB, oyB);
  ctx.restore();
  ctx.globalAlpha = 1;
};


export const drawShred = (
  ctx,
  imgA,
  imgB,
  p,
  canvas,
  scaleA = 1,
  scaleB = 1,
  oxA = 0,
  oyA = 0,
  oxB = 0,
  oyB = 0,
 filterA, filterB
) => {
  const slices = 8;
  const sliceH = canvas.height / slices;

  // Draw A once (editor-aware)
   ctx.save();
 ctx.filter = getCanvasFilter(filterA);

  drawImageFit(ctx, imgA, canvas, scaleA, oxA, oyA);
  ctx.restore();

  for (let i = 0; i < slices; i++) {
    const y = i * sliceH;
    const offset = Math.sin(p * Math.PI * 3 + i) * 50 * p;
    const angle = (p * 2 - 1) * 0.5;

    ctx.save();
    ctx.translate(offset, y + sliceH / 2);
    ctx.rotate(angle);

    ctx.drawImage(
      canvas,
      0, y, canvas.width, sliceH,
      0, -sliceH / 2, canvas.width, sliceH
    );

    ctx.restore();
  }

  ctx.save();
ctx.globalAlpha = p;
ctx.filter = getCanvasFilter(filterB);

  drawImageFit(ctx, imgB, canvas, scaleB, oxB, oyB);
  ctx.restore();
  ctx.globalAlpha = 1;
};




export const drawSpin = (
  ctx,
  imgA,
  imgB,
  p,
  canvas,
  scaleA = 1,
  scaleB = 1,
  oxA = 0,
  oyA = 0,
  oxB = 0,
  oyB = 0,
 filterA, filterB
) => {
  const angle = p * 2 * Math.PI;

  ctx.save();
  ctx.filter = getCanvasFilter(filterA);
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate(angle);
  
  drawImageFit(
    ctx,
    imgA,
    canvas,
    scaleA,
    oxA - canvas.width / 2,
    oyA - canvas.height / 2
  );

  ctx.restore();

  ctx.save();
ctx.globalAlpha = p;
ctx.filter = getCanvasFilter(filterB);
  drawImageFit(ctx, imgB, canvas, scaleB, oxB, oyB);
  ctx.restore();
  ctx.globalAlpha = 1;
};



export const drawZoomThrough = (
  ctx,
  imgA,
  imgB,
  p,
  canvas,
  scaleA = 1,
  scaleB = 1,
  oxA = 0,
  oyA = 0,
  oxB = 0,
  oyB = 0,
 filterA, filterB
) => {
  // Strong cinematic zoom
  const zoom = 1 + p * 2.5;

  // A zooms through (relative to editor zoom)
   ctx.save();
 ctx.filter = getCanvasFilter(filterA);

  drawImageFit(
    ctx,
    imgA,
    canvas,
    scaleA * zoom,
    oxA,
    oyA
  );
  ctx.restore();

  // B fades in after midpoint
  ctx.globalAlpha = Math.max(0, p - 0.3) / 0.7;

  ctx.save();

ctx.filter = getCanvasFilter(filterB);

  drawImageFit(
    ctx,
    imgB,
    canvas,
    scaleB,
    oxB,
    oyB
  );
  ctx.restore();

  ctx.globalAlpha = 1;
};


export const drawLensPop = (
  ctx,
  imgA,
  imgB,
  p,
  canvas,
  scaleA = 1,
  scaleB = 1,
  oxA = 0,
  oyA = 0,
  oxB = 0,
  oyB = 0,
 filterA, filterB
) => {
  const strength = Math.sin(p * Math.PI) * 0.4;
  const w = canvas.width;
  const h = canvas.height;

  // 1️⃣ Draw A with editor transform
   ctx.save();
 ctx.filter = getCanvasFilter(filterA);
  drawImageFit(ctx, imgA, canvas, scaleA, oxA, oyA);
  ctx.restore();

  // 2️⃣ Lens warp on fitted frame
  const src = ctx.getImageData(0, 0, w, h);
  const dst = ctx.createImageData(w, h);

  const s = src.data;
  const d = dst.data;

  const cx = w / 2;
  const cy = h / 2;

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const dx = x - cx;
      const dy = y - cy;
      const dist = Math.sqrt(dx * dx + dy * dy);

      const warp = 1 + strength * (dist / (w * 0.6));

      const sx = Math.min(w - 1, Math.max(0, (dx * warp + cx) | 0));
      const sy = Math.min(h - 1, Math.max(0, (dy * warp + cy) | 0));

      const si = (sy * w + sx) * 4;
      const di = (y * w + x) * 4;

      d[di]     = s[si];
      d[di + 1] = s[si + 1];
      d[di + 2] = s[si + 2];
      d[di + 3] = s[si + 3];
    }
  }

  ctx.putImageData(dst, 0, 0);

  // 3️⃣ Fade in B (editor-aware)
  

ctx.save();
ctx.globalAlpha = p;
ctx.filter = getCanvasFilter(filterB);

  drawImageFit(ctx, imgB, canvas, scaleB, oxB, oyB);
  ctx.restore();
  ctx.globalAlpha = 1;
};



export const drawInkWipe = (
  ctx,
  imgA,
  imgB,
  p,
  canvas,
  scaleA = 1,
  scaleB = 1,
  oxA = 0,
  oyA = 0,
  oxB = 0,
  oyB = 0,
 filterA, filterB
) => {
  // Draw A correctly
   ctx.save();
 ctx.filter = getCanvasFilter(filterA);
  drawImageFit(ctx, imgA, canvas, scaleA, oxA, oyA);
  ctx.restore();

  ctx.save();
  ctx.beginPath();

  const waveCount = 5;
  const amplitude = 50;

  for (let y = 0; y <= canvas.height; y += 10) {
    const x =
      p * canvas.width +
      Math.sin(y * 0.05 + p * 10) * amplitude;

    ctx.lineTo(x, y);
  }

  ctx.lineTo(canvas.width, canvas.height);
  ctx.lineTo(canvas.width, 0);
  ctx.closePath();
  ctx.clip();


  ctx.save();
ctx.filter = getCanvasFilter(filterB);
  drawImageFit(ctx, imgB, canvas, scaleB, oxB, oyB);

  ctx.restore();
};

export const drawFeatheredWipe = (
  ctx,
  imgA,
  imgB,
  p,
  canvas,
  scaleA = 1,
  scaleB = 1,
  oxA = 0,
  oyA = 0,
  oxB = 0,
  oyB = 0
) => {
  // 1️⃣ Draw base A normally
  drawImageFit(ctx, imgA, canvas, scaleA, oxA, oyA);

  // 2️⃣ Create gradient mask
  const feather = 80;
  const wipeX = canvas.width * p;

  const grad = ctx.createLinearGradient(
    wipeX - feather,
    0,
    wipeX + feather,
    0
  );

  grad.addColorStop(0, "rgba(0,0,0,0)");
  grad.addColorStop(1, "rgba(0,0,0,1)");

  // 3️⃣ Draw B with gradient alpha
  ctx.save();
  ctx.globalCompositeOperation = "source-over";
  ctx.fillStyle = grad;

  // Use alpha mask by drawing B, then masking it
  ctx.globalCompositeOperation = "destination-in";

  // Draw B first
  drawImageFit(ctx, imgB, canvas, scaleB, oxB, oyB);

  // Apply feather mask
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.restore();
};



// Radial wipe transition
export const drawRadialWipe = (
  ctx,
  imgA,
  imgB,
  p,
  canvas,
  scaleA = 1,
  scaleB = 1,
  oxA = 0,
  oyA = 0,
  oxB = 0,
  oyB = 0
) => {
  // Draw base A
  drawImageFit(ctx, imgA, canvas, scaleA, oxA, oyA);

  const cx = canvas.width / 2;
  const cy = canvas.height / 2;

  const maxR = Math.hypot(cx, cy);
  const r = maxR * p;

  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.clip();

  drawImageFit(ctx, imgB, canvas, scaleB, oxB, oyB);

  ctx.restore();
};







// Transition map
export const TRANSITIONS = {
  fade: (...args) => drawFade(...args),
  "slide-left": (
    ctx, a, b, p, c,
    scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB
  ) => drawSlide(ctx, a, b, p, c, -1, scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB),

  "slide-right": (
    ctx, a, b, p, c,
    scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB
  ) => drawSlide(ctx, a, b, p, c, 1, scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB),

  "zoom-in": (
    ctx, a, b, p, c,
    scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB
    ) => drawZoom(ctx, a, b, p, c, scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB),

  bounce: (
      ctx, a, b, p, c,
      scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB
    ) => drawBounce(ctx, a, b, p, c, scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB),

  flip: (
      ctx, a, b, p, c,
      scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB
    ) => drawFlip(ctx, a, b, p, c, scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB),

  wipe: (
      ctx, a, b, p, c,
      scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB
    ) => drawWipe(ctx, a, b, p, c, scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB),

  blur: (
      ctx, a, b, p, c,
      scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB
    ) => drawBlurFade(ctx, a, b, p, c, scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB),

  parallax: (
      ctx, a, b, p, c,
      scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB
    ) => drawParallax(ctx, a, b, p, c, scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB),


  "feathered-wipe": (
      ctx, a, b, p, c,
      scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB
    ) => drawFeatheredWipe(ctx, a, b, p, c, scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB),

  "directional-fade": (
      ctx, a, b, p, c,
      scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB
    ) => drawDirectionalFade(ctx, a, b, p, c, scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB),
  "radial-wipe": (
      ctx, a, b, p, c,
      scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB
    ) => drawRadialWipe(ctx, a, b, p, c, scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB),


  // 🔥 GLITCH VARIANTS
  glitch: (
      ctx, a, b, p, c,
      scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB
    ) => drawGlitch(ctx, a, b, p, c, scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB),

  "glitch-standard": (
    ctx, a, b, p, c,
    scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB
  ) => drawGlitchStandard(ctx, a, b, p, c, scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB),

  "glitch-digital": (
    ctx, a, b, p, c,
    scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB
  ) => drawGlitchDigital(ctx, a, b, p, c, scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB),

  "glitch-vhs": (
    ctx, a, b, p, c,
    scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB
  ) => drawGlitchVHS(ctx, a, b, p, c, scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB),

  "glitch-scanline": (
    ctx, a, b, p, c,
    scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB
  ) => drawGlitchScanline(ctx, a, b, p, c, scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB),

  "glitch-analog": (
    ctx, a, b, p, c,
    scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB
  ) => drawGlitchAnalog(ctx, a, b, p, c, scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB),

  "glitch-buffer": (
    ctx, a, b, p, c,
    scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB
  ) => drawGlitchBuffer(ctx, a, b, p, c, scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB),

  // "glitch-multilayer":(ctx, a, b, p, c) => drawGlitchMultilayer(ctx, a, b, p, c),
  fog: (
      ctx, a, b, p, c,
      scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB
    ) => drawFog(ctx, a, b, p, c, scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB),

  "shrink-pop": (
      ctx, a, b, p, c,
      scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB
    ) => drawShrinkPop(ctx, a, b, p, c, scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB),

  "whip-pan": (
      ctx, a, b, p, c,
      scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB
    ) => drawWhipPan(ctx, a, b, p, c, scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB),
  flash: (
      ctx, a, b, p, c,
      scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB
    ) => drawFlash(ctx, a, b, p, c, scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB),

  ripple: (
  ctx, a, b, p, c,
  scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB
) => drawRipple(ctx, a, b, p, c, scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB),

  shred: (
      ctx, a, b, p, c,
      scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB
    ) => drawShred(ctx, a, b, p, c, scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB),

  spin: (
  ctx, a, b, p, c,
  scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB
) => drawSpin(ctx, a, b, p, c, scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB),

  "zoom-through": (
  ctx, a, b, p, c,
  scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB
) => drawZoomThrough(ctx, a, b, p, c, scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB),

  "lens-pop": (
  ctx, a, b, p, c,
  scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB
) => drawLensPop(ctx, a, b, p, c, scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB),

  "ink-wipe": (
      ctx, a, b, p, c,
      scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB
    ) => drawInkWipe(ctx, a, b, p, c, scaleA, scaleB, oxA, oyA, oxB, oyB, filterA, filterB),

};

